import { useState } from "react";
import { useResource } from "../lib/hooks";
import { api } from "../lib/api";
import { uploadFile } from "../lib/files";

interface Doc {
    id: string; document_type_id: string | null; type_name: string | null;
    reference_number: string | null; issue_date: string | null; expiry_date: string | null;
    verification_status: string; days_remaining: number | null;
    status: "none" | "current" | "expiring" | "expired";
}
interface DocType { id: string; name: string; expires: boolean }

const STATUS_CHIP: Record<Doc["status"], string> = { current: "pass", expiring: "warn", expired: "block", none: "mute" };
const fmt = (d: string | null) => (d ? new Date(d).toLocaleDateString("en-AU") : "—");

export function MyDocuments() {
    const docs = useResource<{ documents: Doc[]; renewalNeeded: Doc[] }>("/me/documents");
    const types = useResource<DocType[]>("/document-types");
    const [typeId, setTypeId] = useState("");
    const [expiry, setExpiry] = useState("");
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const chosen = (types.data ?? []).find((t) => t.id === typeId);

    const upload = async (file: File) => {
        setError(null);
        if (!typeId) { setError("Choose a document type first."); return; }
        if (chosen?.expires && !expiry) { setError("This document type needs an expiry date."); return; }
        setBusy(true);
        try {
            const stored = await uploadFile(file, "general");
            await api.post("/me/documents", { fileId: stored.id, documentTypeId: typeId, expiryDate: chosen?.expires ? expiry : null });
            setTypeId(""); setExpiry("");
            docs.reload();
        } catch (e) {
            setError(e instanceof Error ? e.message : "Upload failed");
        } finally { setBusy(false); }
    };

    const renewals = docs.data?.renewalNeeded ?? [];

    return (
        <div className="card">
            <div className="card-head"><h2>My documents</h2></div>
            <div className="card-body">
                {renewals.length > 0 && (
                    <div className="error-banner" style={{ background: "var(--warn-bg)", color: "var(--warn)" }}>
                        Renewal needed: {renewals.map((r) => r.type_name).join(", ")}. Upload a new copy below — it replaces the old one automatically.
                    </div>
                )}

                {(docs.data?.documents ?? []).length === 0 ? (
                    <p className="muted">No documents on file yet.</p>
                ) : (
                    <table style={{ width: "100%", marginBottom: "0.8rem" }}>
                        <thead><tr>
                            <th style={{ textAlign: "left" }}>Document</th><th>Expiry</th><th>Verification</th><th>Status</th>
                        </tr></thead>
                        <tbody>
                            {docs.data!.documents.map((d) => (
                                <tr key={d.id}>
                                    <td>{d.type_name ?? "—"}{d.reference_number ? <span className="muted"> · {d.reference_number}</span> : ""}</td>
                                    <td style={{ textAlign: "center" }}>{fmt(d.expiry_date)}</td>
                                    <td style={{ textAlign: "center" }}>
                                        <span className={`chip ${d.verification_status === "verified" ? "pass" : "warn"}`}>{d.verification_status}</span>
                                    </td>
                                    <td style={{ textAlign: "center" }}><span className={`chip ${STATUS_CHIP[d.status]}`}>{d.status}</span></td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}

                {error && <div className="error-banner">{error}</div>}
                <div className="toolbar" style={{ gap: "0.5rem", flexWrap: "wrap" }}>
                    <select value={typeId} onChange={(e) => setTypeId(e.target.value)}>
                        <option value="">Document type…</option>
                        {(types.data ?? []).map((t) => <option key={t.id} value={t.id}>{t.name}{t.expires ? " (expires)" : ""}</option>)}
                    </select>
                    {chosen?.expires && (
                        <input type="date" aria-label="Expiry date" value={expiry} onChange={(e) => setExpiry(e.target.value)} />
                    )}
                    <label className="btn ghost small">
                        {busy ? "Uploading…" : "Upload / renew"}
                        <input type="file" hidden disabled={busy}
                            onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }} />
                    </label>
                </div>
            </div>
        </div>
    );
}
